import { HStack, Stack, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";

import {
  CampaignCriteria,
  CampaignCriteriaKind,
  forkCampaignCriteria,
  patchCampaignCriteria,
} from "../api/campaign-criteria";
import { patchKeywordAgent } from "../api/keyword-agent";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { brand } from "../lib/brand";
import { Button } from "./button";
import {
  CampaignCriteriaFields,
  CampaignCriteriaFormData,
  campaignCriteriaSchema,
  criteriaToFormValues,
  formValuesToRequest,
} from "./campaign-criteria-fields";
import { CampaignCriteriaPresetPicker } from "./campaign-criteria-preset-picker";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { toaster } from "./toaster";

interface EditCampaignCriteriaDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  workspaceSlug: string;
  customerId: string;
  loginCustomerId: string | null;
  campaignId: number;
  adGroupId: number;
  criteria: CampaignCriteria;
}

export function EditCampaignCriteriaDialog({
  adGroupId,
  campaignId,
  criteria,
  customerId,
  isOpen,
  loginCustomerId,
  onOpenChange,
  onSuccess,
  workspaceSlug,
}: EditCampaignCriteriaDialogProps) {
  const [isPicking, setIsPicking] = React.useState(false);
  const [pickedPreset, setPickedPreset] =
    React.useState<CampaignCriteria | null>(null);
  const [isSaving, withSaving] = useLoading();
  const withToaster = useToaster();

  // The preset in play, picked just now or already the ad group's.
  // Either way the answers are shared, which the hint and footer key off.
  const preset =
    pickedPreset ??
    (criteria.kind === CampaignCriteriaKind.PRESET ? criteria : null);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<CampaignCriteriaFormData>({
    resolver: zodResolver(campaignCriteriaSchema),
    defaultValues: criteriaToFormValues(criteria),
  });

  React.useEffect(() => {
    if (isOpen) {
      reset(criteriaToFormValues(criteria));
    }
  }, [criteria, isOpen, reset]);

  // Closing clears the pick, so reopening starts from what the ad group
  // actually reads rather than a choice that was never saved.
  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      setIsPicking(false);
      setPickedPreset(null);
    }
    onOpenChange(details);
  };

  const close = () => handleOpenChange({ open: false });

  // Resetting re-baselines the form, so isDirty afterwards means "edited
  // away from the preset".
  const applyPreset = (p: CampaignCriteria | null) => {
    if (!p) {
      setPickedPreset(null);
      return;
    }
    reset(criteriaToFormValues(p));
    setPickedPreset(p);
    setIsPicking(false);
  };

  const finish = () => {
    close();
    onSuccess();
  };

  // Two acts once a preset is in play, which is why the footer asks
  // which: the answers can land on the preset — moving this ad group onto
  // it, and reaching every other one already there — or stay with this ad
  // group alone.
  const saveToPreset = handleSubmit(async (data) => {
    if (!preset) return;
    await withSaving(async () => {
      await withToaster(async () => {
        await patchCampaignCriteria(
          workspaceSlug,
          preset.id,
          formValuesToRequest(data),
        );
        if (criteria.id !== preset.id) {
          await patchKeywordAgent(
            workspaceSlug,
            customerId,
            loginCustomerId,
            campaignId,
            adGroupId,
            preset.id,
          );
        }
        toaster.create({
          type: "success",
          title: "Preset updated — every ad group using it follows",
        });
        finish();
      });
    });
  });

  const saveForThisAdGroup = handleSubmit(async (data) => {
    await withSaving(async () => {
      await withToaster(async () => {
        const request = formValuesToRequest(data);
        // A shared preset can't take answers meant for one ad group, so
        // they go to a copy this agent alone points at. An ephemeral
        // criteria already is that copy, whichever preset the answers
        // were read out of.
        if (criteria.kind === CampaignCriteriaKind.PRESET) {
          const fork = await forkCampaignCriteria(workspaceSlug, criteria.id, {
            name: pickedPreset?.name ?? criteria.name,
          });
          await patchCampaignCriteria(workspaceSlug, fork.id, request);
          await patchKeywordAgent(
            workspaceSlug,
            customerId,
            loginCustomerId,
            campaignId,
            adGroupId,
            fork.id,
          );
        } else {
          await patchCampaignCriteria(workspaceSlug, criteria.id, request);
        }
        toaster.create({
          type: "success",
          title: preset
            ? "Criteria updated for this ad group only"
            : "Criteria updated",
        });
        finish();
      });
    });
  });

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange} size="lg">
      <DialogContent>
        <DialogCloseTrigger />
        {isPicking ? (
          <>
            <DialogHeader>
              <Stack gap="1">
                <DialogTitle>Select from preset</DialogTitle>
                <DialogDescription>
                  This ad group will use the preset, including any changes you
                  make to it later.
                </DialogDescription>
              </Stack>
            </DialogHeader>
            <DialogBody>
              <CampaignCriteriaPresetPicker
                workspaceSlug={workspaceSlug}
                selectedId={preset?.id ?? null}
                onSelectionChange={applyPreset}
                emptyText="No presets yet. You can save answers as a preset when you turn the agent on for an ad group."
              />
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsPicking(false)}>
                Back
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              {/* The header lays its children out in a row, so the stack
                  has to claim the width before the button can sit at the
                  end of the title line. Its padding clears the close
                  button, which is absolutely positioned in that corner. */}
              <Stack gap="1" width="full">
                <HStack gap="4" justify="space-between" paddingEnd="8">
                  <DialogTitle>Campaign criteria</DialogTitle>
                  <Button
                    size="sm"
                    variant="surface"
                    flexShrink="0"
                    onClick={() => setIsPicking(true)}
                  >
                    Select from preset
                  </Button>
                </HStack>
                <DialogDescription>
                  {brand.productName} evaluates every search term based on these
                  answers.
                </DialogDescription>
              </Stack>
            </DialogHeader>
            <DialogBody>
              <Stack gap="4">
                {preset && (
                  <Text color="fg.muted" fontSize="sm">
                    From the preset &ldquo;{preset.name}&rdquo;. Saving to the
                    preset changes every ad group using it.{" "}
                    {isDirty && (
                      <>Your edits haven&rsquo;t been saved to it yet.</>
                    )}
                  </Text>
                )}
                <CampaignCriteriaFields control={control} errors={errors} />
                <Text color="fg.muted" fontSize="xs" lineHeight={"1.5" as any}>
                  Changes apply going forward. Terms {brand.productName} has
                  already decided on won&rsquo;t change.
                </Text>
              </Stack>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" disabled={isSaving} onClick={close}>
                Cancel
              </Button>
              {preset && (
                <Button
                  variant="surface"
                  loading={isSaving}
                  onClick={saveToPreset}
                >
                  Save to preset
                </Button>
              )}
              <Button
                variant="solid"
                loading={isSaving}
                onClick={saveForThisAdGroup}
              >
                {preset ? "Save for this ad group only" : "Save"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
